import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Paper,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Drawer,
  Grid,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  GetApp as DownloadIcon,
  CloudUpload as UploadIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  HelpOutline as HelpIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Lock as LockIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import FormBuilderSteps from "../../components/FormBuilder/FormBuilderSteps";
import RoleManager from "../../components/FormBuilder/RoleManager";
import HardenedFieldsManager from "../../components/FormBuilder/HardenedFieldsManager";
import FormGuidelines from "../../components/FormBuilder/FormGuidelines";
import {
  createForm,
  getFormsByRole,
  updateForm,
  deleteForm,
  publishForm,
  duplicateForm,
  exportFormAsJSON,
  importFormFromJSON,
  updateRole,
  deleteRole,
} from "../../services/formService";
import {
  validateFormSchema,
  generateFormJSON,
} from "../../utils/formSchemaUtils";
import "./FormManager.scss";
import { getRequestWithToken } from "../../utils/ApiRequests";
import { checkPermission } from "../../utils/userPermissions";
import Unauthorized from "../../components/unauthorized/Unauthorized";

const FormManager = () => {
  const [forms, setForms] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [showRoleManager, setShowRoleManager] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [showDeleteRoleConfirm, setShowDeleteRoleConfirm] = useState(false);
  const [deleteRoleId, setDeleteRoleId] = useState(null);
  const [currentForm, setCurrentForm] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [formSteps, setFormSteps] = useState([]);
  const [editingFormId, setEditingFormId] = useState(null);
  const [newFormRole, setNewFormRole] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [selectedRole, setSelectedRole] = useState("");
  const [activeOverviewTab, setActiveOverviewTab] = useState("forms");
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [showHardenedManager, setShowHardenedManager] = useState(false);

  const [canCreate, setCanCreate] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);

  const [isAccess, setisAccess] = useState([]);
  const [isIncludesValue, setisIncludesValue] = useState(null);

  // Load forms on component mount and when role changes
  useEffect(() => {
    loadForms(selectedRole || null);
  }, [selectedRole]);

  // Load all forms or forms by role
  const loadForms = async (role = null) => {
    setLoading(true);
    try {
      const data = await getFormsByRole(role);
      // Handle different response formats
      let formsData = [];
      if (Array.isArray(data)) {
        formsData = data;
      } else if (data.data && Array.isArray(data.data)) {
        formsData = data.data;
      }
      else if (data?.data?.forms && Array.isArray(data?.data?.forms)) {
        formsData = data.data.forms;

      } else if (data.success && Array.isArray(data.result)) {
        formsData = data.result;
      }
      setForms(formsData);
      // showSnackbar(data?.msg, "success");
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to load forms";
      showSnackbar(errorMsg, "error");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle opening create form dialog
  const handleOpenCreateDialog = () => {
    setEditingFormId(null);
    setCurrentForm(null);
    setFormFields([]);
    setFormSteps([]);
    setNewFormRole("");
    setShowCreateDialog(true);
  };

  // Handle opening edit form dialog
  const handleEditForm = (form) => {
    console.log(form, " form to be edited");
    setEditingFormId(form._id || form.id);
    setCurrentForm(form);
    setFormFields(form.fields || []);
    setFormSteps(form.steps || []);
    // Set role with both name and _id if available, otherwise just the name
    if (form.roleId || form.role_id) {
      setNewFormRole(JSON.stringify({ name: form.role, _id: form.roleId || form.role_id }));
    } else {
      setNewFormRole(form.role);
    }
    setShowCreateDialog(true);
  };

  // Handle saving form (create/update)
  const handleSaveForm = async () => {
    if (!newFormRole.trim()) {
      showSnackbar("Please select a role", "error");
      return;
    }

    // Check if we're using steps or fields
    const isStepBased = formSteps.length > 0;

    if (!isStepBased && formFields.length === 0) {
      showSnackbar("Please add at least one field or step", "error");
      return;
    }

    if (isStepBased && formSteps.length === 0) {
      showSnackbar("Please add at least one step", "error");
      return;
    }

    try {
      // Parse role object (it's stored as JSON string with {name, _id})
      let roleObj;
      try {
        roleObj = typeof newFormRole === "string" ? JSON.parse(newFormRole) : newFormRole;
      } catch (e) {
        // Fallback if it's just a string (for backward compatibility)
        roleObj = { name: newFormRole };
      }

      // Validate form schema - use role name for validation
      if (!isStepBased) {
        const validation = validateFormSchema({
          role: roleObj.name,
          fields: formFields,
        });

        if (!validation.isValid) {
          showSnackbar(validation.error, "error");
          return;
        }
      }

      // Generate form JSON with role name, then add role._id
      const formData = isStepBased
        ? generateFormJSON(roleObj.name, [], currentForm?.version || 1, formSteps)
        : generateFormJSON(roleObj.name, formFields, currentForm?.version || 1);

      // Strip isHardened flags before sending to API (frontend-only marker)
      if (formData.steps) {
        formData.steps.forEach((step) => {
          delete step.isHardened;
          if (step.fields) {
            step.fields.forEach((field) => {
              delete field.isHardened;
            });
          }
        });
      }

      // Add role _id to the payload if it exists
      if (roleObj._id) {
        formData.role_id = roleObj._id;
      }

      console.log(formData, " form data to be saved");

      if (editingFormId) {
        // Update existing form - exclude role from update
        const updateData = { ...formData };
        delete updateData.role;
        delete updateData.version;
        delete updateData.role_id;
        const response = await updateForm(editingFormId, updateData);
        showSnackbar(getResponseMessage(response, "Form updated successfully"), "success");
      } else {
        // Create new form
        const response = await createForm("dynamic-form", formData);
        showSnackbar(getResponseMessage(response, "Form created successfully"), "success");
      }

      setShowCreateDialog(false);
      loadForms(selectedRole);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to save form";
      showSnackbar(errorMsg, "error");
      console.error(error);
    }
  };

  // Handle opening delete confirmation
  const handleOpenDeleteConfirm = (formId) => {
    setDeleteTargetId(formId);
    setShowDeleteConfirmDialog(true);
  };

  // Handle deleting form
  const handleDeleteForm = async () => {
    try {
      const response = await deleteForm(deleteTargetId);
      showSnackbar(getResponseMessage(response, "Form deleted successfully"), "success");
      setShowDeleteConfirmDialog(false);
      setDeleteTargetId(null);
      loadForms(selectedRole);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to delete form";
      showSnackbar(errorMsg, "error");
      console.error(error);
    }
  };

  // Handle opening preview dialog
  const handlePreviewForm = (form) => {
    setCurrentForm(form);
    setShowPreviewDialog(true);
  };

  // Handle duplicating form
  const handleDuplicateForm = async (form) => {
    try {
      const response = await duplicateForm(form._id || form.id);
      showSnackbar(getResponseMessage(response, "Form duplicated successfully"), "success");
      loadForms(selectedRole);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to duplicate form";
      showSnackbar(errorMsg, "error");
      console.error(error);
    }
  };

  // Handle exporting form
  const handleExportForm = (form) => {
    try {
      exportFormAsJSON(form);
      showSnackbar("Form exported successfully", "success");
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to export form";
      showSnackbar(errorMsg, "error");
      console.error(error);
    }
  };

  // Handle creating new role
  const handleCreateRole = async (roleData) => {
    try {
      const response = await createForm("plus-role", roleData);
      showSnackbar(getResponseMessage(response, "Role created successfully"), "success");
      setShowRoleManager(false);
      setEditingRole(null);
      loadAllRoles();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to create role";
      showSnackbar(errorMsg, "error");
      console.error("Error creating role:", error);
    }
  };

  // Handle updating role
  const handleUpdateRole = async (roleId, roleData) => {
    try {
      const response = await updateRole(roleId, roleData);
      showSnackbar(getResponseMessage(response, "Role updated successfully"), "success");
      setShowRoleManager(false);
      setEditingRole(null);
      loadAllRoles();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to update role";
      showSnackbar(errorMsg, "error");
      console.error("Error updating role:", error);
    }
  };

  // Handle opening edit role dialog
  const handleEditRole = (role) => {
    setEditingRole(role);
    setShowRoleManager(true);
  };

  // Handle opening delete role confirmation
  const handleOpenDeleteRoleConfirm = (roleId) => {
    setDeleteRoleId(roleId);
    setShowDeleteRoleConfirm(true);
  };

  // Handle deleting role
  const handleDeleteRole = async () => {
    try {
      const response = await deleteRole(deleteRoleId);
      showSnackbar(getResponseMessage(response, "Role deleted successfully"), "success");
      setShowDeleteRoleConfirm(false);
      setDeleteRoleId(null);
      loadAllRoles();
      // If the deleted role was selected, clear the selection
      const deletedRole = availableRoles.find(r => r._id === deleteRoleId);
      if (deletedRole && selectedRole === deletedRole.name) {
        setSelectedRole("");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to delete role";
      showSnackbar(errorMsg, "error");
      console.error("Error deleting role:", error);
    }
  };

  // Handle importing form
  const handleImportForm = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const form = await importFormFromJSON(file);

      if (!form.role || (!form.fields && !form.steps)) {
        throw new Error("Invalid form format");
      }

      const response = await createForm("dynamic-form", form);
      showSnackbar(getResponseMessage(response, "Form imported successfully"), "success");
      loadForms(form.role);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to import form";
      showSnackbar(errorMsg, "error");
      console.error(error);
    }
  };

  // Handle publishing form
  const handlePublishToggle = async (form) => {
    try {
      const newStatus = !form.isActive;
      const response = await publishForm(form._id || form.id, newStatus);
      showSnackbar(
        getResponseMessage(response, `Form ${newStatus ? "activated" : "deactivated"} successfully`),
        "success"
      );
      loadForms(selectedRole);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to update form status";
      showSnackbar(errorMsg, "error");
      console.error(error);
    }
  };

  const loadAllRoles = async () => {
    try {
      const rolesData = await getRequestWithToken("admin/plus-roles?page=1&limit=100");
      setAvailableRoles(rolesData?.data?.data?.plusRoles || []);
      showSnackbar(rolesData?.data?.message || "Roles loaded successfully", "success");
    }
    catch (error) {
      console.error("Failed to load roles", error);
    }
  };


  useEffect(() => {
    loadAllRoles();
  }, []);

  // Extract message from API response
  const getResponseMessage = (response, defaultMessage) => {
    return response?.message || response?.msg || response?.data?.message || response?.data?.msg || defaultMessage;
  };

  // Show snackbar notification
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const getLiveFormUrl = (form) => {
    const baseUrl = import.meta.env.VITE_ENV === "production"
      ? "https://jobsyahan.com"
      : "https://staging.jobsyahan.com";
    return `${baseUrl}/en/plus/${form.role}/register`;
  };

  const copyText = (text, successMessage) => {
    navigator.clipboard
      .writeText(text)
      .then(() => showSnackbar(successMessage, "success"))
      .catch((error) => showSnackbar(error?.message || "Failed to copy", "error"));
  };

  // Open guidelines in new tab
  const openGuidelinesInNewTab = () => {
    // Open guidelines page in new tab
    window.open('/jyplus/form-guidelines', '_blank');
  };

  const checkPermissionFunc = () => {
    const resourceToUpdate = "Plus-tech";
    const permissions = checkPermission(resourceToUpdate);
    setCanCreate(permissions.canCreate);
    setCanDelete(permissions.canDelete);
    setCanUpdate(permissions.canUpdate);
  };

  const againCheckPermissions = () => {
    const checkAccesInArray = localStorage.getItem("resourceNames");
    setisAccess(checkAccesInArray);
    isIncludes();
  };

  const isIncludes = () => {
    setisIncludesValue(isAccess.includes("Plus-tech"));
  };

  useEffect(() => {
    checkPermissionFunc();
    againCheckPermissions();
  }, [
    isIncludesValue,
  ]);

  return isIncludesValue ? (
    <Box className="form-manager-container" sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Stack spacing={3}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                JY Plus Form Builder
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<LockIcon />}
                onClick={() => setShowHardenedManager(true)}
                size="small"
                color="warning"
              >
                System Fields
              </Button>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setShowRoleManager(true)}
                size="small"
                disabled={!canCreate}
              >
                New Role
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={!canCreate}
                onClick={handleOpenCreateDialog}
              >
                Create Form
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                component="label"
                size="small"
              >
                Import
                <input
                  type="file"
                  accept=".json"
                  hidden
                  onChange={handleImportForm}
                />
              </Button>
              <Button
                variant="outlined"
                startIcon={<HelpIcon />}
                onClick={() => setShowGuidelines(true)}
                size="small"
                color="info"
              >
                Guidelines
              </Button>
              <IconButton
                size="small"
                color="info"
                title="Open Guidelines in New Tab"
                onClick={openGuidelinesInNewTab}
                sx={{
                  border: "1px solid",
                  borderColor: "info.main",
                  borderRadius: 1,
                  height: 36,
                  width: 36,
                  alignSelf: "center",
                }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>


          <Paper variant="elevation" sx={{ p: 1, display: 'flex', alignItems: "center", justifyContent: "flex-start", gap: 4 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Role Filter
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" fullWidth style={{ widht: "500px" }}>
              <FormControl sx={{ minWidth: 200 }} fullWidth>
                <InputLabel>Select Role</InputLabel>
                <Select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  label="Select Role"
                >
                  <MenuItem value="">-- All Roles --</MenuItem>
                  {availableRoles.map((role) => (
                    <MenuItem key={role._id} value={role.name}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedRole && (
                <Stack direction="row" spacing={1}>
                  <IconButton
                    size="small"
                    color="primary"
                    title="Edit Role"
                    onClick={() => {
                      const role = availableRoles.find(r => r.name === selectedRole);
                      if (role) handleEditRole(role);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    title="Delete Role"
                    onClick={() => {
                      const role = availableRoles.find(r => r.name === selectedRole);
                      if (role) handleOpenDeleteRoleConfirm(role._id);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          </Paper>

        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tabs
            value={activeOverviewTab}
            onChange={(event, value) => setActiveOverviewTab(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Forms Overview" value="forms" />
            <Tab label="Role Directory" value="roles" />
          </Tabs>
          <Chip
            label={activeOverviewTab === "forms" ? `${forms.length} forms` : `${availableRoles.length} roles`}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ alignSelf: { xs: "flex-start", sm: "center" }, mb: { xs: 1, sm: 0 } }}
          />
        </Stack>

        {activeOverviewTab === "forms" && (
          <>
            {loading ? (
              <Box className="loading-container">
                <CircularProgress />
              </Box>
            ) : forms.length === 0 ? (
              <Paper className="empty-state" variant="outlined" sx={{ p: 3 }}>
                <Typography color="textSecondary">
                  {selectedRole
                    ? `No forms found for the "${selectedRole}" role`
                    : "No forms available"}
                </Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow className="table-header">
                      <TableCell>Role</TableCell>
                      <TableCell>Version</TableCell>
                      <TableCell align="center">Structure</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {forms.map((form) => (
                      <TableRow key={form._id || form.id} hover>
                        <TableCell className="role-cell">
                          <Stack spacing={0.3}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {form.role}
                            </Typography>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography variant="caption" color="text.secondary">
                                Updated v{form.version || 1}
                              </Typography>
                              <Tooltip title="Copy live form URL">
                                <IconButton
                                  style={{ width: '20px', height: "20px" }}
                                  title="Copy live form URL"
                                  onClick={() => copyText(getLiveFormUrl(form), "Live form URL copied")}
                                  sx={{ p: 0.25, widht: 20, height: 20 }}
                                >
                                  <ContentCopyIcon sx={{ fontSize: 20, color: "#ff8c00" }} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Stack>
                        </TableCell>
                        <TableCell>v{form.version || 1}</TableCell>
                        <TableCell align="center">
                          {form.steps && form.steps.length > 0 ? (
                            <Chip
                              label={`${form.steps.length} steps`}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          ) : (
                            <Chip
                              label={`${form.fields?.length || 0} fields`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={form.isActive ? "Active" : "Inactive"}
                            color={form.isActive ? "success" : "default"}
                            size="small"
                            onClick={() => handlePublishToggle(form)}
                            clickable
                          />
                        </TableCell>
                        <TableCell align="center" className="actions-cell">
                          <IconButton
                            size="small"
                            title="Preview"
                            onClick={() => handlePreviewForm(form)}
                          >
                            <ViewIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            title="Edit"
                            disabled={!canUpdate}
                            onClick={() => handleEditForm(form)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            title="Export"
                            onClick={() => handleExportForm(form)}
                          >
                            <DownloadIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            title="Delete"
                            disabled={!canDelete}
                            onClick={() => handleOpenDeleteConfirm(form._id || form.id)}
                            className="delete-icon"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {activeOverviewTab === "roles" && (
          <>
            {availableRoles.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No roles available.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow className="table-header">
                      <TableCell>Role</TableCell>
                      <TableCell>_id</TableCell>
                      <TableCell align="center">Copy</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {availableRoles.map((role) => (
                      <TableRow key={role._id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {role.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            component="code"
                            sx={{
                              display: "block",
                              maxWidth: { xs: 180, sm: 320, md: 560 },
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={role._id}
                          >
                            {role._id}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Copy role ID">
                            <IconButton
                              size="small"
                              onClick={() => copyText(role._id, "Role ID copied")}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Paper>

      {/* Create/Edit Form Dialog */}
      <Dialog
        open={showCreateDialog}
        onClose={(event, reason) => {
          // Only close if Cancel button is clicked (reason will be undefined)
          // Block closing from backdrop (reason = "backdropClick")
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            setShowCreateDialog(false);
          }
        }}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingFormId ? "Edit Form" : "Create New Form"}
        </DialogTitle>
        <DialogContent className="form-dialog-content">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
            {editingFormId ? (
              // When editing, show role as a text field instead of select
              <TextField
                label="Role"
                value={
                  typeof newFormRole === "string"
                    ? (() => {
                      try {
                        const roleObj = JSON.parse(newFormRole);
                        return roleObj.name || newFormRole;
                      } catch {
                        return newFormRole;
                      }
                    })()
                    : newFormRole
                }
                fullWidth
                disabled
                variant="outlined"
              />
            ) : (
              // When creating, show role as a select
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={newFormRole}
                  onChange={(e) => setNewFormRole(e.target.value)}
                  label="Role"
                >
                  {availableRoles.map((role) => (
                    <MenuItem key={role._id} value={JSON.stringify({ name: role.name, _id: role._id })}>
                      {role.name.toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {newFormRole && (
              <FormBuilderSteps
                key={editingFormId || 'new'}
                initialSteps={formSteps}
                onStepsChange={setFormSteps}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveForm}
            variant="contained"
            color="primary"
          >
            {editingFormId ? "Update Form" : "Create Form"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Form Dialog */}
      <Dialog
        open={showPreviewDialog}
        onClose={(event, reason) => {
          // Only close if Close button is clicked (reason will be undefined)
          // Block closing from backdrop (reason = "backdropClick")
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            setShowPreviewDialog(false);
          }
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Preview Form - {currentForm?.role}</DialogTitle>
        <DialogContent>
          {currentForm && (
            <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Step-based form preview */}
              {currentForm.steps && currentForm.steps.length > 0 ? (
                currentForm.steps.map((step, stepIndex) => (
                  <Box key={stepIndex} sx={{ mb: 3 }}>
                    <Paper sx={{ p: 2, bgcolor: "#f5f5f5", mb: 2 }}>
                      <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600, mb: 0.5 }}>
                        Step {stepIndex + 1}: {step.title}
                      </Typography>
                      {step.description && (
                        <Typography variant="body2" sx={{ color: "#666" }}>
                          {step.description}
                        </Typography>
                      )}
                    </Paper>

                    {/* Fields in this step */}
                    {step.fields && step.fields.length > 0 ? (
                      step.fields.map((field, fieldIndex) => (
                        <Box key={fieldIndex} sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              display: "flex",
                              gap: 0.5,
                              alignItems: "center",
                            }}
                          >
                            {field.label}
                            {field.required && <span style={{ color: "red" }}>*</span>}
                          </Typography>

                          {/* Field Preview based on type */}
                          {field.type === "text" && (
                            <TextField placeholder={field.label} fullWidth disabled size="small" variant="outlined" />
                          )}
                          {field.type === "email" && (
                            <TextField placeholder={field.label} fullWidth disabled size="small" type="email" variant="outlined" />
                          )}
                          {field.type === "number" && (
                            <TextField placeholder={field.label} fullWidth disabled size="small" type="number" variant="outlined" />
                          )}
                          {field.type === "textarea" && (
                            <TextField placeholder={field.label} fullWidth disabled size="small" multiline rows={3} variant="outlined" />
                          )}
                          {field.type === "dropdown" && (
                            <Select disabled displayEmpty size="small">
                              <MenuItem value=""><em>Select {field.label}</em></MenuItem>
                              {field.options?.map((option, idx) => (
                                <MenuItem key={idx} value={option.value}>{option.label}</MenuItem>
                              ))}
                            </Select>
                          )}
                          {field.type === "radio" && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                              {field.options?.map((option, idx) => (
                                <FormControlLabel key={idx} disabled control={<input type="radio" disabled />} label={option.label} />
                              ))}
                            </Box>
                          )}
                          {field.type === "multiselect" && (
                            <FormControlLabel disabled control={<input type="multiselect" disabled />} label={field.label} />
                          )}
                          {field.type === "boolean" && (
                            <Box sx={{ display: "flex", gap: 2 }}>
                              <FormControlLabel disabled control={<input type="radio" disabled />} label="Yes" />
                              <FormControlLabel disabled control={<input type="radio" disabled />} label="No" />
                            </Box>
                          )}
                          {field.type === "date" && (
                            <TextField placeholder={field.label} fullWidth disabled size="small" type="date" variant="outlined" InputLabelProps={{ shrink: true }} />
                          )}
                          {field.type === "file" && (
                            <Box sx={{ border: "2px dashed #ccc", borderRadius: 1, p: 2, textAlign: "center", backgroundColor: "#f9f9f9", cursor: "not-allowed", opacity: 0.7 }}>
                              <Typography variant="body2" color="textSecondary">📁 Click to upload {field.label}</Typography>
                            </Box>
                          )}
                          {field.type === "multiSelectCheckbox" && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              {field.maxSelection > 0 && (
                                <Typography variant="caption" sx={{ color: "#666" }}>Max selections: {field.maxSelection}</Typography>
                              )}
                              {field.enableSearch && (
                                <TextField placeholder="Search..." fullWidth disabled size="small" variant="outlined" />
                              )}
                              {field.enableSections && field.sections ? (
                                field.sections.map((section, sIdx) => (
                                  <Box key={sIdx}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1, mb: 0.5, fontSize: "0.85rem" }}>
                                      {section.name}
                                    </Typography>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                      {section.options?.map((opt, oIdx) => (
                                        <FormControlLabel key={oIdx} disabled control={<input type="checkbox" disabled />} label={opt.label} />
                                      ))}
                                    </Box>
                                  </Box>
                                ))
                              ) : (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                  {field.options?.map((opt, oIdx) => (
                                    <FormControlLabel key={oIdx} disabled control={<input type="checkbox" disabled />} label={opt.label} />
                                  ))}
                                </Box>
                              )}
                            </Box>
                          )}
                          {field.type === "year" && (
                            <Box sx={{ border: "1px solid #ddd", borderRadius: 1, p: 1.5, bgcolor: "#fafafa" }}>
                              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                                {field.includeDate && (
                                  <TextField size="small" disabled value="01" sx={{ width: 80 }} />
                                )}
                                {field.includeMonth && (
                                  <TextField size="small" disabled value="January" sx={{ width: 140 }} />
                                )}
                                <TextField size="small" disabled value={String(field.yearStart || new Date().getFullYear())} sx={{ width: 100 }} />
                              </Box>
                              {field.customOptions && field.customOptions.length > 0 && (
                                <Typography variant="caption" sx={{ display: "block", mt: 1, color: "#666" }}>
                                  Custom: {field.customOptions.map(o => o.label).join(", ")}
                                </Typography>
                              )}
                            </Box>
                          )}

                          <Typography variant="caption" color="textSecondary">
                            Field Key: <code>{field.key}</code>
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Alert severity="info" sx={{ mb: 2 }}>No fields in this step</Alert>
                    )}
                  </Box>
                ))
              ) : currentForm.fields ? (
                /* Fallback: Field-based form preview */
                currentForm.fields.map((field, index) => (
                  <Box key={index} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        display: "flex",
                        gap: 0.5,
                        alignItems: "center",
                      }}
                    >
                      {field.label}
                      {field.required && <span style={{ color: "red" }}>*</span>}
                    </Typography>

                    {field.type === "text" && (
                      <TextField placeholder={field.label} fullWidth disabled size="small" variant="outlined" />
                    )}
                    {field.type === "email" && (
                      <TextField placeholder={field.label} fullWidth disabled size="small" type="email" variant="outlined" />
                    )}
                    {field.type === "number" && (
                      <TextField placeholder={field.label} fullWidth disabled size="small" type="number" variant="outlined" />
                    )}
                    {field.type === "textarea" && (
                      <TextField placeholder={field.label} fullWidth disabled size="small" multiline rows={3} variant="outlined" />
                    )}
                    {field.type === "dropdown" && (
                      <Select disabled displayEmpty size="small">
                        <MenuItem value=""><em>Select {field.label}</em></MenuItem>
                        {field.options?.map((option, idx) => (
                          <MenuItem key={idx} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </Select>
                    )}
                    {field.type === "radio" && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {field.options?.map((option, idx) => (
                          <FormControlLabel key={idx} disabled control={<input type="radio" disabled />} label={option.label} />
                        ))}
                      </Box>
                    )}
                    {field.type === "multiselect" && (
                      <FormControlLabel disabled control={<input type="multiselect" disabled />} label={field.label} />
                    )}
                    {field.type === "boolean" && (
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <FormControlLabel disabled control={<input type="radio" disabled />} label="Yes" />
                        <FormControlLabel disabled control={<input type="radio" disabled />} label="No" />
                      </Box>
                    )}
                    {field.type === "date" && (
                      <TextField placeholder={field.label} fullWidth disabled size="small" type="date" variant="outlined" InputLabelProps={{ shrink: true }} />
                    )}
                    {field.type === "file" && (
                      <Box sx={{ border: "2px dashed #ccc", borderRadius: 1, p: 2, textAlign: "center", backgroundColor: "#f9f9f9", cursor: "not-allowed", opacity: 0.7 }}>
                        <Typography variant="body2" color="textSecondary">📁 Click to upload {field.label}</Typography>
                      </Box>
                    )}
                    {field.type === "multiSelectCheckbox" && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {field.maxSelection > 0 && (
                          <Typography variant="caption" sx={{ color: "#666" }}>Max selections: {field.maxSelection}</Typography>
                        )}
                        {field.enableSearch && (
                          <TextField placeholder="Search..." fullWidth disabled size="small" variant="outlined" />
                        )}
                        {field.enableSections && field.sections ? (
                          field.sections.map((section, sIdx) => (
                            <Box key={sIdx}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1, mb: 0.5, fontSize: "0.85rem" }}>
                                {section.name}
                              </Typography>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                {section.options?.map((opt, oIdx) => (
                                  <FormControlLabel key={oIdx} disabled control={<input type="checkbox" disabled />} label={opt.label} />
                                ))}
                              </Box>
                            </Box>
                          ))
                        ) : (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            {field.options?.map((opt, oIdx) => (
                              <FormControlLabel key={oIdx} disabled control={<input type="checkbox" disabled />} label={opt.label} />
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                    {field.type === "year" && (
                      <Box sx={{ border: "1px solid #ddd", borderRadius: 1, p: 1.5, bgcolor: "#fafafa" }}>
                        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                          {field.includeDate && (
                            <TextField size="small" disabled value="01" sx={{ width: 80 }} />
                          )}
                          {field.includeMonth && (
                            <TextField size="small" disabled value="January" sx={{ width: 140 }} />
                          )}
                          <TextField size="small" disabled value={String(field.yearStart || new Date().getFullYear())} sx={{ width: 100 }} />
                        </Box>
                        {field.customOptions && field.customOptions.length > 0 && (
                          <Typography variant="caption" sx={{ display: "block", mt: 1, color: "#666" }}>
                            Custom: {field.customOptions.map(o => o.label).join(", ")}
                          </Typography>
                        )}
                      </Box>
                    )}

                    <Typography variant="caption" color="textSecondary">
                      Field Key: <code>{field.key}</code>
                    </Typography>
                  </Box>
                ))
              ) : (
                <Alert severity="warning">No form structure available</Alert>
              )}

              {/* Form metadata */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  borderLeft: "4px solid #1976d2",
                }}
              >
                <Typography variant="caption" display="block">
                  <strong>Role:</strong> {currentForm.role}
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>Version:</strong> {currentForm.version}
                </Typography>
                {currentForm.steps && currentForm.steps.length > 0 ? (
                  <Typography variant="caption" display="block">
                    <strong>Total Steps:</strong> {currentForm.steps.length}
                  </Typography>
                ) : (
                  <Typography variant="caption" display="block">
                    <strong>Total Fields:</strong> {currentForm.fields?.length || 0}
                  </Typography>
                )}
                <Typography variant="caption" display="block">
                  <strong>Status:</strong>{" "}
                  <Chip
                    label={currentForm.isActive ? "Active" : "Inactive"}
                    color={currentForm.isActive ? "success" : "default"}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreviewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirmDialog}
        onClose={(event, reason) => {
          // Only close if Cancel button is clicked (reason will be undefined)
          // Block closing from backdrop (reason = "backdropClick")
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            setShowDeleteConfirmDialog(false);
          }
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>Delete Form</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this form? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirmDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteForm}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* RoleManager Dialog */}
      <RoleManager
        open={showRoleManager}
        onClose={() => {
          setShowRoleManager(false);
          setEditingRole(null);
        }}
        onCreateRole={handleCreateRole}
        editingRole={editingRole}
        onUpdateRole={handleUpdateRole}
      />

      {/* HardenedFieldsManager Drawer */}
      <HardenedFieldsManager
        open={showHardenedManager}
        canCreate={canCreate}
        canUpdate={canUpdate}
        canDelete={canDelete}
        onClose={() => setShowHardenedManager(false)}
      />

      {/* Delete Role Confirmation Dialog */}
      <Dialog
        open={showDeleteRoleConfirm}
        onClose={(event, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            setShowDeleteRoleConfirm(false);
          }
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this role? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteRoleConfirm(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteRole}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Guidelines Drawer */}
      <Drawer
        anchor="right"
        open={showGuidelines}
        onClose={() => setShowGuidelines(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: '400px', md: '400px' },
            boxSizing: 'border-box',
          },
        }}
      >
        {/* Drawer Header */}
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: "2px solid #e0e0e0",
          bgcolor: "#f5f5f5",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HelpIcon color="info" fontSize="large" />
            <Typography variant="h5" fontWeight={600}>Form Builder Guidelines</Typography>
          </Box>
          <IconButton onClick={() => setShowGuidelines(false)} size="medium">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Drawer Content */}
        <Box sx={{ p: 3, overflowY: "auto", height: "100%" }}>
          <FormGuidelines />
        </Box>

        {/* Drawer Footer */}
        <Box sx={{
          p: 2,
          borderTop: "2px solid #e0e0e0",
          bgcolor: "#f5f5f5",
          position: "sticky",
          bottom: 0,
          display: "flex",
          justifyContent: "flex-end"
        }}>
          <Button
            // onClick={() => setShowGuidelines(false)}
            onClick={() => handleOpenCreateDialog()}
            variant="contained"
            size="large"
            fullWidth
          >
            Got it! Create Form
          </Button>
        </Box>
      </Drawer>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  ) : (
    <Unauthorized />
  );
};

export default FormManager;
